import { atom, useAtom, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

export type NotificationType =
	| "success"
	| "error"
	| "warning"
	| "info"
	| "none";

export type NotificationOptions = {
	closable?: boolean;
	autoClose?: boolean;
	autoCloseDelay?: number;
	/**
	 * Optional grouping identifier. Notifications with a scope are not rendered
	 * as global toasts by the NotificationProvider; a feature can render them
	 * inline instead (e.g. the shipment-management error banner).
	 */
	scope?: string;
};

export type NotificationItem = {
	id: string;
	type: NotificationType;
	message: string;
	closable?: boolean;
	autoClose?: boolean;
	autoCloseDelay?: number;
	scope?: string;
};

// Global atom for notifications
const notificationsAtom = atom<NotificationItem[]>([]);

/**
 * Hook to access notification actions only, without subscribing to notification state.
 * This prevents unnecessary re-renders when notifications are added/removed.
 */
export function useNotificationActions() {
	const setNotifications = useSetAtom(notificationsAtom);

	const closeNotification = useCallback(
		(id: string) => {
			setNotifications((prev) =>
				prev.filter((notification) => notification.id !== id),
			);
		},
		[setNotifications],
	);

	const showNotification = useCallback(
		(
			type: NotificationType,
			message: string,
			options?: NotificationOptions,
		) => {
			const id = uuidv4();

			const defaultDelay = type === "success" ? 2000 : 15_000;

			const newNotification: NotificationItem = {
				id,
				type,
				message,
				closable: options?.closable ?? true,
				autoClose: options?.autoClose ?? true,
				autoCloseDelay: options?.autoCloseDelay ?? defaultDelay,
				scope: options?.scope,
			};

			setNotifications((prev) => [...prev, newNotification]);

			// Auto-close notification if enabled
			if (newNotification.autoClose) {
				setTimeout(() => {
					closeNotification(id);
				}, newNotification.autoCloseDelay);
			}

			return id;
		},
		[setNotifications, closeNotification],
	);

	const clearAllNotifications = useCallback(() => {
		setNotifications([]);
	}, [setNotifications]);

	const showSuccess = useCallback(
		(message: string, options?: Parameters<typeof showNotification>[2]) =>
			showNotification("success", message, options),
		[showNotification],
	);

	const showError = useCallback(
		(message: string, options?: Parameters<typeof showNotification>[2]) =>
			showNotification("error", message, options),
		[showNotification],
	);

	const showWarning = useCallback(
		(message: string, options?: Parameters<typeof showNotification>[2]) =>
			showNotification("warning", message, options),
		[showNotification],
	);

	const showInfo = useCallback(
		(message: string, options?: Parameters<typeof showNotification>[2]) =>
			showNotification("info", message, options),
		[showNotification],
	);

	return useMemo(
		() => ({
			showNotification,
			showSuccess,
			showError,
			showWarning,
			showInfo,
			closeNotification,
			clearAllNotifications,
		}),
		[
			showNotification,
			showSuccess,
			showError,
			showWarning,
			showInfo,
			closeNotification,
			clearAllNotifications,
		],
	);
}

export function useNotifications() {
	const [notifications] = useAtom(notificationsAtom);
	const actions = useNotificationActions();

	return useMemo(
		() => ({
			notifications,
			...actions,
		}),
		[notifications, actions],
	);
}
